import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const DifficultyDonut = ({ data }) => {
  // Enhanced gradient colors matching the CategoryProgress theme
  const difficultyColors = {
    Easy: {
      background: 'rgba(107, 115, 255, 0.8)',
      border: '#6b73ff',
      hover: 'rgba(107, 115, 255, 0.9)'
    },
    Medium: {
      background: 'rgba(139, 92, 246, 0.8)',
      border: '#8b5cf6',
      hover: 'rgba(139, 92, 246, 0.9)'
    },
    Hard: {
      background: 'rgba(168, 85, 247, 0.8)',
      border: '#a855f7',
      hover: 'rgba(168, 85, 247, 0.9)'
    },
  };

  const createGradient = (ctx, startColor, endColor) => {
    const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 200);
    gradient.addColorStop(0, startColor);
    gradient.addColorStop(1, endColor);
    return gradient;
  };

  const chartData = {
    labels: data.map(item => item._id),
    datasets: [
      {
        data: data.map(item => item.count),
        backgroundColor: function(context) {
          const chart = context.chart;
          const {ctx} = chart;
          const label = context.label;
          const colors = difficultyColors[label];
          if (!colors || !ctx) {
            return colors?.background || 'rgba(107, 115, 255, 0.8)';
          }
          return createGradient(ctx, colors.background, colors.border);
        },
        borderColor: data.map(item => difficultyColors[item._id]?.border || '#6b73ff'),
        borderWidth: 3,
        hoverOffset: 8,
        hoverBackgroundColor: data.map(item => difficultyColors[item._id]?.hover || 'rgba(107, 115, 255, 0.9)'),
        hoverBorderColor: data.map(item => difficultyColors[item._id]?.border || '#6b73ff'),
        hoverBorderWidth: 4,
        // Add shadow effect
        shadowOffsetX: 0,
        shadowOffsetY: 4,
        shadowBlur: 12,
        shadowColor: 'rgba(107, 115, 255, 0.3)',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
    },
    animation: {
      duration: 800,
      easing: 'easeOutQuart',
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 24,
          usePointStyle: true,
          pointStyle: 'circle',
          color: '#6b7280',
          font: {
            size: 13,
            weight: '500',
          },
          generateLabels: function(chart) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const meta = chart.getDatasetMeta(0);
                const style = meta.controller.getStyle(i);
                const value = data.datasets[0].data[i];
                const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                
                return {
                  text: `${label} (${percentage}%)`,
                  fillStyle: difficultyColors[label]?.border || style.backgroundColor,
                  strokeStyle: style.borderColor,
                  lineWidth: style.borderWidth,
                  pointStyle: 'circle',
                  hidden: isNaN(value) || meta.data[i].hidden,
                  index: i
                };
              });
            }
            return [];
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#4b5563',
        borderColor: 'rgba(107, 115, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 12,
        displayColors: false,
        padding: 12,
        titleFont: {
          size: 14,
          weight: '600',
        },
        bodyFont: {
          size: 13,
          weight: '500',
        },
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `Problems Solved: ${value}`;
          },
          afterLabel: function(context) {
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${percentage}% of total`;
          },
        },
        external: function(context) {
          // Custom tooltip styling for dark mode
          const tooltip = context.tooltip;
          if (document.documentElement.classList.contains('dark')) {
            context.tooltip.options.backgroundColor = 'rgba(31, 41, 55, 0.95)';
            context.tooltip.options.titleColor = '#f9fafb';
            context.tooltip.options.bodyColor = '#d1d5db';
            context.tooltip.options.borderColor = 'rgba(107, 115, 255, 0.3)';
          }
        },
      },
    },
    cutout: '65%',
    // Add hover animations
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
    },
  };

  const total = data.reduce((sum, item) => sum + item.count, 0);
  const topDifficulty = data.length > 0 ? data.reduce((prev, current) => 
    (prev.count > current.count) ? prev : current
  ) : null;

  return (
    <div className="card">
      {/* Enhanced Header */}
      <div className="card-header">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Difficulty Distribution
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Problems solved by difficulty level
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gradient">
              {total}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Total Problems
            </div>
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="card-body">
        <div className="relative h-80">
          <Doughnut data={chartData} options={options} />
          
          {/* Center content with enhanced styling */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <div className="text-3xl font-bold text-gradient mb-1">
                {total}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Total Problems
              </div>
            </div>
          </div>

          {/* Overlay gradient for visual enhancement */}
          <div className="absolute inset-0 pointer-events-none rounded-2xl bg-gradient-to-t from-white/5 to-transparent dark:from-gray-900/10"></div>
        </div>

        {/* Enhanced Stats Footer */}
        {topDifficulty && (
          <div className="mt-6 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Most Common: <span className="text-primary-600 dark:text-primary-400 font-semibold">
                    {topDifficulty._id}
                  </span>
                </span>
              </div>
              <div className="text-sm text-blue-600 dark:text-gray-400">
                {topDifficulty.count} problems ({((topDifficulty.count / total) * 100).toFixed(1)}%)
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DifficultyDonut;